import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const team = [
  {
    id: "ira-roy-chowdhury",
    name: "Ira Roy Chowdhury",
    role: "Proprietor and Founder",
    image:
      "/team/ira.png",
    bio:
      "Ira leads Prithira Global with a vision rooted in sustainability and ethical sourcing. With deep ties to India's agricultural communities, she champions the transformation of natural fibers into responsible packaging for global markets. Her leadership shapes every partnership and product.",
  },
  {
    id: "abhishek-sengupta",
    name: "Abhishek Sengupta",
    role: "Co-Founder",
    image:
      "/team/abhishek.jpg",
    bio:
      "Abhishek manages export operations and client relationships. With a deep commitment to sustainability and responsible trade, he drives the company's mission of bringing eco-friendly packaging from Indian soil to global tables.",
  },
  {
    id: "anirban-roy-chowdhury",
    name: "Anirban Roy Chowdhury",
    role: "Co-Founder",
    image:
      "/team/anirban.jpg",
    bio:
      "Anirban oversees domestic as well as overseas market development and supply chain operations, focusing on building sustainable partnerships across the globe.",
  },
];

const TeamPage = () => {
  return (
    <div data-testid="team-page" className="noise-overlay">
      <Helmet>
        <title>Meet the Team | Prithira Global Leadership</title>
        <meta
          name="description"
          content="Meet the team behind Prithira Global — Ira Roy Chowdhury (Proprietor and Founder), Abhishek Sengupta and Anirban Roy Chowdhury (Co-Founders), leading our eco-friendly packaging mission."
        />
        <meta
          name="keywords"
          content="prithira global team, eco packaging founders India, sustainable supplier leadership, biodegradable exporter team"
        />
        <link rel="canonical" href="https://www.prithiraglobal.com/team" />
        <meta property="og:title" content="Meet the Team | Prithira Global" />
        <meta
          property="og:description"
          content="The leadership behind Prithira Global's eco-friendly packaging mission."
        />
        <meta property="og:url" content="https://www.prithiraglobal.com/team" />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-20 bg-sand-200/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <span className="caption text-primary mb-4 block">Our People</span>
            <h1 className="heading-xl text-stone-900 mb-8">
              Meet the <span className="italic">Team</span>
            </h1>
            <p className="body-lg text-stone-600 max-w-3xl mx-auto">
              The minds and hands behind Prithira Global — bringing together vision,
              trade expertise, and a shared belief in plastic-free packaging.
            </p>
          </div>
        </div>
      </section>

      {/* Team Cards */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 max-w-6xl mx-auto">
            {team.map((member) => (
              <article
                key={member.id}
                data-testid={`team-card-${member.id}`}
                className="group flex flex-col items-center text-center bg-sand-200/30 hover:bg-sand-200/60 transition-colors rounded-2xl p-8 lg:p-10"
              >
                <div className="relative mb-6">
                  <div className="w-40 h-40 lg:w-48 lg:h-48 rounded-full overflow-hidden ring-4 ring-white shadow-md">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 inline-block w-12 h-1 bg-primary rounded-full" />
                </div>

                <h2
                  data-testid={`team-name-${member.id}`}
                  className="font-heading text-xl lg:text-2xl text-stone-900 font-bold mb-2"
                >
                  {member.name}
                </h2>
                <p className="font-heading italic text-primary text-sm lg:text-base mb-5">
                  {member.role}
                </p>
                <p className="text-stone-600 font-light text-sm leading-relaxed">
                  {member.bio}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-primary text-white">
        <div className="container-custom text-center">
          <h2 className="heading-lg mb-6">
            Partner With <span className="italic">Us</span>
          </h2>
          <p className="body-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Have a project in mind, or want to bring biodegradable packaging
            to your business? We'd love to hear from you.
          </p>
          <Link to="/partner">
            <Button
              data-testid="team-partner-btn"
              className="rounded-full px-8 py-6 text-base font-medium bg-white text-primary hover:bg-stone-100 btn-lift"
            >
              Get in Touch
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
